import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/use-auth';
import { trackEvent } from '../utils/analytics';
import type { User } from '@supabase/supabase-js';
import { ArrowLeft, CalendarDays, CheckCircle2, Lock } from 'lucide-react';
import { formatTimeOfDay, formatWeekdayDateDisplay } from '../utils/date';
import { normalizeMeetingMode, type SignupMeetingMode } from '../utils/signupSlots';
import { eligibleStudents, type EligibilityCandidate } from '../utils/signupEligibility';
import {
  describeLockWindow,
  formatLockDeadline,
  hasSlotStarted,
  isSignupLockError,
  isSlotBookable,
  isSlotLocked,
  slotLockDeadline,
} from '../utils/signupLock';
import { useNow } from '../hooks/useNow';
import { MeetingModeBadge } from '../components/signup/MeetingModeBadge';

type MeetingType = 'in_person' | 'phone_call' | 'zoom' | 'google_meet';

interface SignupEvent {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  created_at: string;
  max_signups_per_slot?: number;
  target_grades: string[] | null;
  /** NULL = parents can change a booking right up to the start. */
  lock_hours_before?: number | null;
}

interface SignupSlot {
  id: string;
  event_id: string;
  slot_date: string;
  start_time: string;
  end_time: string;
  meeting_mode: SignupMeetingMode | null;
  is_available: boolean;
  signup_registrations: { id: string }[];
}

interface SignupRegistration {
  id: string;
  slot_id: string;
  parent_id: string;
  student_id: string;
  parent_name: string;
  parent_email: string;
  student_name: string;
  phone: string | null;
  notes: string | null;
  meeting_type: MeetingType;
  created_at: string;
  signup_slots?: {
    id: string;
    event_id: string;
    slot_date: string;
    start_time: string;
    end_time: string;
    meeting_mode: SignupMeetingMode | null;
    signup_events?: {
      id: string;
      title: string;
    };
  };
  students?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

interface Parent {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  active: boolean;
  metadata: { grade?: string | null } | null;
}

/** A description longer than this is folded on first view; the slots are what a parent came for. */
const LONG_DESCRIPTION_CHARS = 220;

const formatTime = (timeStr: string) => formatTimeOfDay(timeStr, { hour12: true });

/** "Mon, Oct 19 · 9:00 – 9:20 AM" style, for a booking. */
const formatSlot = (slot: { slot_date: string; start_time: string; end_time: string }) =>
  `${formatWeekdayDateDisplay(slot.slot_date)} · ${formatTime(slot.start_time)} – ${formatTime(slot.end_time)}`;

export default function AuthenticatedSignupPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user, isLoading: authLoading } = useAuth();

  // Early return if no eventId
  if (!eventId) {
    return <Navigate to="/" replace />;
  }

  // Early return if auth is loading
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Early return if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AuthenticatedContent eventId={eventId} userEmail={user?.email} user={user} />;
}

function AuthenticatedContent({ eventId, userEmail, user }: { eventId: string; userEmail: string | undefined; user: User | null }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const now = useNow();
  const [selectedEventId, setSelectedEventId] = useState<string>(eventId || '');
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [selectedSlotId, setSelectedSlotId] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [showFullDescription, setShowFullDescription] = useState(false);
  // What just happened, in words, shown above the bookings. The form it came from may have gone
  // (the last child was just booked), so it cannot live inside the form.
  const [notice, setNotice] = useState<string>('');
  // Set while a parent is moving an existing booking; the slot picker then writes to this
  // registration instead of creating a new one.
  const [movingRegistrationId, setMovingRegistrationId] = useState<string>('');
  const [notes, setNotes] = useState('');
  // How a meeting is held is the school's decision, not the parent's: it is set on the slot
  // when the round is built, and the booking simply records it. Slots from before per-slot
  // modes existed carry no mode, and those stay in-person exactly as they always were — which
  // is why no flag guards this. A slot with no mode renders exactly as it always has.

  // Following a link from one round to another within the app reuses this component.
  useEffect(() => {
    if (eventId) setSelectedEventId(eventId);
  }, [eventId]);

  // Fetch all active events
  const { data: allEvents = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['signup-events'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('signup_events')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SignupEvent[];
    },
  });

  // Set default selected event if none selected and we have events
  useEffect(() => {
    if (!selectedEventId && allEvents.length > 0) {
      setSelectedEventId(eventId || allEvents[0].id);
    }
  }, [allEvents, selectedEventId, eventId]);

  // Get the currently selected event
  const selectedEvent = allEvents.find(e => e.id === selectedEventId);
  const lockHours = selectedEvent?.lock_hours_before ?? null;

  // If URL eventId is inactive/invalid (not in active events), prevent loading slots for it.
  // Allow parent to pick an active event via the event selector instead.
  useEffect(() => {
    if (!eventsLoading && selectedEventId && allEvents.length > 0 && !selectedEvent) {
      // Keep behavior simple: fall back to the newest active event.
      setSelectedEventId(allEvents[0].id);
    }
  }, [eventsLoading, selectedEventId, allEvents, selectedEvent]);

  // Fetch parent
  const { data: parent, isLoading: parentLoading } = useQuery({
    queryKey: ['parent', userEmail],
    queryFn: async () => {
      if (!userEmail) throw new Error('No user email');

      const { data, error } = await supabase
        .from('parents')
        .select('*')
        .eq('email', userEmail)
        .single();

      if (error) throw error;
      return data as Parent;
    },
    enabled: !!userEmail,
  });

  // Fetch students
  const { data: students = [], isLoading: studentsLoading } = useQuery({
    queryKey: ['parent-students', parent?.id],
    queryFn: async () => {
      if (!parent?.id) return [];

      const { data, error } = await supabase
        .from('student_parents')
        .select(`
          students!inner (
            id,
            first_name,
            last_name,
            date_of_birth,
            active,
            metadata
          )
        `)
        .eq('parent_id', parent.id)
        .eq('can_pay', true)
        .eq('students.active', true);

      if (error) throw error;
      return data.map(item => item.students).flat() as Student[];
    },
    enabled: !!parent?.id,
  });

  // Fetch slots for selected event
  const { data: allSlots = [], isLoading: slotsLoading } = useQuery({
    queryKey: ['signup-slots', selectedEventId],
    queryFn: async () => {
      if (!selectedEventId) return [];

      const { data, error } = await supabase
        .from('signup_slots')
        .select(`
          *,
          signup_registrations (id)
        `)
        .eq('event_id', selectedEventId)
        .order('slot_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (error) throw error;
      return data as SignupSlot[];
    },
    enabled: !!selectedEventId && !!selectedEvent,
  });

  // Build student id list for broader registration visibility
  const studentIds = useMemo(() => students.map((s) => s.id), [students]);

  // Which kinds of programme each child is actually enrolled in right now. A round can be
  // limited to, say, after-school families, and the link is shareable — so the page has to
  // apply the same rule the dashboard does rather than trusting whoever forwarded it.
  const { data: activeProgramTypesByStudent = {} } = useQuery<Record<string, string[]>>({
    queryKey: ['signup-eligibility-enrollments', studentIds.join(',')],
    queryFn: async () => {
      if (studentIds.length === 0) return {};
      const { data, error } = await supabase
        .from('enrollments')
        .select('student_id, programs!inner (type, active)')
        .in('student_id', studentIds)
        .eq('is_active', true);

      if (error) throw error;

      // PostgREST returns an embedded one-to-one as an object or a single-element array
      // depending on how it resolves the relationship; accept both rather than guess.
      type ProgramRef = { type: string | null; active: boolean };
      const rows = (data ?? []) as unknown as { student_id: string; programs: ProgramRef | ProgramRef[] | null }[];

      const byStudent: Record<string, string[]> = {};
      for (const row of rows) {
        const programs = Array.isArray(row.programs) ? row.programs : row.programs ? [row.programs] : [];
        for (const program of programs) {
          if (!program.active || !program.type) continue;
          (byStudent[row.student_id] ??= []).push(program.type);
        }
      }
      return byStudent;
    },
    enabled: studentIds.length > 0,
  });

  // Fetch existing registrations for all of this parent's students (across parents)
  const { data: existingRegistrations = [], isLoading: registrationsLoading } = useQuery({
    queryKey: ['student-registrations', studentIds.join(',')],
    queryFn: async () => {
      if (studentIds.length === 0) return [];

      const { data, error } = await supabase
        .from('signup_registrations')
        .select(`
          *,
          signup_slots!inner (
            id,
            event_id,
            slot_date,
            start_time,
            end_time,
            meeting_mode,
            signup_events!inner (
              id,
              title,
              is_active
            )
          ),
          students (
            id,
            first_name,
            last_name
          )
        `)
        .in('student_id', studentIds)
        .eq('signup_slots.signup_events.is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SignupRegistration[];
    },
    enabled: studentIds.length > 0,
  });

  // This page is one round. Bookings in another round live on that round's page and on the
  // list — showing them here put a second round's Cancel button under this round's title.
  const thisEventRegistrations = useMemo(
    () => existingRegistrations
      .filter((r) => r.signup_slots?.event_id === selectedEventId)
      .sort((a, b) =>
        (a.signup_slots?.slot_date ?? '').localeCompare(b.signup_slots?.slot_date ?? '')
        || (a.signup_slots?.start_time ?? '').localeCompare(b.signup_slots?.start_time ?? '')),
    [existingRegistrations, selectedEventId]
  );

  // A student gets one slot per event — the same rule the unique index enforces in the
  // database. It used to be read as one slot across *all* events, which quietly locked a child
  // out of a new round because they had a slot in an older one that was still active.
  // Only the children this round is for, and only the ones who have not booked yet.
  const eligibleForEvent = useMemo(() => {
    if (!selectedEvent) return [];
    const candidates: (EligibilityCandidate & { student: Student })[] = students.map((student) => ({
      id: student.id,
      firstName: student.first_name,
      active: student.active,
      grade: student.metadata?.grade ?? null,
      activeProgramTypes: activeProgramTypesByStudent[student.id] ?? [],
      student,
    }));

    return eligibleStudents(candidates, {
      id: selectedEvent.id,
      title: selectedEvent.title,
      endDate: selectedEvent.end_date,
      targetGrades: selectedEvent.target_grades,
    }).map((candidate) => candidate.student);
  }, [students, selectedEvent, activeProgramTypesByStudent]);

  const availableStudents = useMemo(() => {
    const registeredIds = new Set(thisEventRegistrations.map((r) => r.student_id));
    return eligibleForEvent.filter((s) => !registeredIds.has(s.id));
  }, [eligibleForEvent, thisEventRegistrations]);

  // If current selection becomes invalid due to filtering, reset it; with exactly one child
  // left to book there is nothing to choose, so choose them.
  useEffect(() => {
    if (selectedStudentId && !availableStudents.some((s) => s.id === selectedStudentId)) {
      setSelectedStudentId('');
    } else if (!selectedStudentId && availableStudents.length === 1) {
      setSelectedStudentId(availableStudents[0].id);
    }
  }, [availableStudents, selectedStudentId]);

  // The slots a parent can pick: room left, not started, and not inside the round's lock.
  const availableSlots = useMemo(() => {
    const maxPerSlot = selectedEvent?.max_signups_per_slot ?? 1;
    return allSlots.filter((slot) =>
      (slot.signup_registrations?.length || 0) < maxPerSlot && isSlotBookable(slot, lockHours, now));
  }, [allSlots, selectedEvent?.max_signups_per_slot, lockHours, now]);

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (data: {
      slotId: string;
      studentId: string;
      parentId: string;
      notes: string;
      parentName: string;
      parentEmail: string;
      studentName: string;
      meetingType: MeetingType;
    }) => {
      const { error } = await supabase
        .from('signup_registrations')
        .insert({
          slot_id: data.slotId,
          student_id: data.studentId,
          parent_id: data.parentId,
          parent_name: data.parentName,
          parent_email: data.parentEmail,
          student_name: data.studentName,
          notes: data.notes,
          meeting_type: data.meetingType,
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('This student is already registered for a slot in this event. Please cancel the existing registration first.');
        }
        // Raised by enforce_signup_change_lock when the slot crossed its lock while open here.
        if (isSignupLockError(error)) throw new Error(error.message);
        if (/just been taken/i.test(error.message)) {
          throw new Error('That time has just been taken. Please choose another.');
        }
        throw error;
      }

      trackEvent({
        eventType: 'signup_registration_create',
        user,
        userType: 'parent',
        entityType: 'signup_slot',
        entityId: data.slotId,
        metadata: {
          event_id: selectedEventId,
          event_name: selectedEvent?.title ?? null,
          student_id: data.studentId,
          student_name: data.studentName,
          meeting_type: data.meetingType,
        },
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['signup-slots', selectedEventId] });
      queryClient.invalidateQueries({ queryKey: ['student-registrations'] });
      const slot = allSlots.find((s) => s.id === variables.slotId);
      setNotice(slot ? `${variables.studentName} is booked for ${formatSlot(slot)}.` : 'Booked.');
      setSelectedSlotId('');
      setSelectedStudentId('');
      setNotes('');
      scrollToTop();
    },
  });

  // Cancel registration mutation
  const cancelRegistrationMutation = useMutation({
    mutationFn: async ({ registrationId, studentName }: { registrationId: string; studentName: string }) => {
      // Deleting frees the slot for someone else, and the row is gone — an admin asking
      // later who released it had nothing to read. The admin-side delete has always been
      // logged (`admin_signup_registration_delete`); the parent's own cancel was not.
      const { data, error } = await supabase
        .from('signup_registrations')
        .delete()
        .eq('id', registrationId)
        .select('id, slot_id');

      if (error) {
        if (isSignupLockError(error)) throw new Error(error.message);
        throw error;
      }

      if ((data?.length ?? 0) > 0) {
        trackEvent({
          eventType: 'signup_registration_cancel',
          user,
          userType: 'parent',
          entityType: 'signup_slot',
          entityId: (data![0] as { slot_id?: string }).slot_id,
          metadata: {
            event_id: selectedEventId,
            event_name: selectedEvent?.title ?? null,
            registration_id: registrationId,
            student_name: studentName,
          },
        });
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['signup-slots', selectedEventId] });
      queryClient.invalidateQueries({ queryKey: ['student-registrations'] });
      setNotice(`${variables.studentName}'s booking was cancelled.`);
    },
  });

  // Moving is one UPDATE, deliberately not a cancel followed by a booking. Cancelling frees the
  // slot the instant it happens, so the two-step version can lose a family their place to
  // somebody quicker between the clicks. The row travels; the slot is never released.
  const moveRegistrationMutation = useMutation({
    mutationFn: async ({ registrationId, slotId, studentName }: { registrationId: string; slotId: string; studentName: string }) => {
      const { error } = await supabase
        .from('signup_registrations')
        .update({ slot_id: slotId })
        .eq('id', registrationId);

      if (error) {
        // The lock shares the capacity rule's SQLSTATE, so it is told apart by its words first.
        if (isSignupLockError(error)) throw new Error(error.message);
        // Raised by enforce_signup_slot_capacity when somebody got there first.
        if (error.code === '23514' || /just been taken/i.test(error.message)) {
          throw new Error('That time has just been taken. Please choose another.');
        }
        throw error;
      }

      trackEvent({
        eventType: 'signup_registration_move',
        user,
        userType: 'parent',
        entityType: 'signup_slot',
        entityId: slotId,
        metadata: {
          event_id: selectedEventId,
          event_name: selectedEvent?.title ?? null,
          registration_id: registrationId,
          student_name: studentName,
        },
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['signup-slots', selectedEventId] });
      queryClient.invalidateQueries({ queryKey: ['student-registrations'] });
      const slot = allSlots.find((s) => s.id === variables.slotId);
      setNotice(slot ? `${variables.studentName} is now booked for ${formatSlot(slot)}.` : 'Your time has been changed.');
      setMovingRegistrationId('');
      setSelectedSlotId('');
      scrollToTop();
    },
  });

  const handleCancelRegistration = (registrationId: string, studentName: string, slotInfo: string) => {
    if (window.confirm(`Are you sure you want to cancel ${studentName}'s registration for ${slotInfo}?`)) {
      setNotice('');
      cancelRegistrationMutation.mutate({ registrationId, studentName });
    }
  };

  const movingRegistration = thisEventRegistrations.find((r) => r.id === movingRegistrationId) ?? null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setNotice('');

    if (movingRegistration) {
      if (!selectedSlotId) return;
      moveRegistrationMutation.mutate({
        registrationId: movingRegistration.id,
        slotId: selectedSlotId,
        studentName: `${movingRegistration.students?.first_name ?? ''} ${movingRegistration.students?.last_name ?? ''}`.trim(),
      });
      return;
    }

    if (!selectedEventId || !selectedStudentId || !selectedSlotId || !parent?.id || !userEmail) {
      return;
    }

    const selectedStudent = students.find(s => s.id === selectedStudentId);
    if (!selectedStudent) {
      return;
    }

    const slot = availableSlots.find((s) => s.id === selectedSlotId);
    const meetingType: MeetingType = normalizeMeetingMode(slot?.meeting_mode) ?? 'in_person';

    registerMutation.mutate({
      slotId: selectedSlotId,
      studentId: selectedStudentId,
      parentId: parent.id,
      parentName: `${parent.first_name} ${parent.last_name}`,
      parentEmail: userEmail,
      studentName: `${selectedStudent.first_name} ${selectedStudent.last_name}`,
      notes,
      meetingType,
    });
  };

  // A moved booking keeps its own slot in the picker, so a parent can see where they are and
  // change their mind. Everything else full is still filtered out.
  const pickableSlots = useMemo(() => {
    if (!movingRegistration) return availableSlots;
    const current = allSlots.find((s) => s.id === movingRegistration.slot_id);
    return current && !availableSlots.some((s) => s.id === current.id)
      ? [...availableSlots, current].sort((a, b) =>
          a.slot_date.localeCompare(b.slot_date) || a.start_time.localeCompare(b.start_time))
      : availableSlots;
  }, [availableSlots, allSlots, movingRegistration]);

  // One day at a time: a five-day round is fifty slots, which on a phone is a scroll nobody
  // finishes. The days are the chips; the slots are the selected day's.
  const slotsByDate = useMemo(() => {
    const grouped = new Map<string, SignupSlot[]>();
    for (const slot of pickableSlots) {
      const list = grouped.get(slot.slot_date);
      if (list) list.push(slot);
      else grouped.set(slot.slot_date, [slot]);
    }
    return grouped;
  }, [pickableSlots]);
  const days = useMemo(() => [...slotsByDate.keys()].sort(), [slotsByDate]);

  useEffect(() => {
    if (days.length === 0) return;
    if (!days.includes(selectedDay)) setSelectedDay(days[0]);
  }, [days, selectedDay]);

  const daySlots = slotsByDate.get(selectedDay) ?? [];
  const selectedSlot = pickableSlots.find((s) => s.id === selectedSlotId) ?? null;

  function scrollToTop() {
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  }

  // Leaving a move clears its error too; otherwise "That time has just been taken" lingers over
  // the booking form it no longer applies to.
  const stopMove = () => {
    setMovingRegistrationId('');
    setSelectedSlotId('');
    moveRegistrationMutation.reset();
  };

  const startMove = (registration: SignupRegistration) => {
    setNotice('');
    moveRegistrationMutation.reset();
    setMovingRegistrationId(registration.id);
    setSelectedSlotId('');
    const currentDay = registration.signup_slots?.slot_date;
    if (currentDay && days.includes(currentDay)) setSelectedDay(currentDay);
    document.getElementById('slot-picker')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const goBack = () => {
    // Back to wherever the parent came from in the app (usually the dashboard); a link opened
    // from an email has no history here, so it goes to the list of rounds instead.
    if (location.key !== 'default') navigate(-1);
    else navigate('/signup');
  };

  // Loading state
  if (eventsLoading || parentLoading || studentsLoading || registrationsLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading signup information...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (allEvents.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">No Active Events</h1>
          <p className="mt-2 text-gray-600">There are no active signup events available at this time.</p>
        </div>
      </div>
    );
  }

  if (!parent) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Parent Profile Not Found</h1>
          <p className="mt-2 text-gray-600">We couldn't find your parent profile. Please contact support.</p>
        </div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-yellow-600">No Active Students</h1>
          <p className="mt-2 text-gray-600">You don't have any active students associated with your account.</p>
        </div>
      </div>
    );
  }

  const description = selectedEvent?.description ?? '';
  const descriptionIsLong = description.length > LONG_DESCRIPTION_CHARS;
  const everyoneBooked = eligibleForEvent.length > 0 && availableStudents.length === 0;
  const showForm = !!movingRegistration || availableStudents.length > 0;
  const submitError = registerMutation.isError
    ? registerMutation.error?.message || 'Failed to register'
    : moveRegistrationMutation.isError
      ? moveRegistrationMutation.error?.message || 'Could not change the time'
      : null;

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 pb-6 sm:px-0">
      <div className="space-y-3">
        <button
          type="button"
          onClick={goBack}
          className="-ml-1 inline-flex items-center gap-1 rounded-md px-1 py-1 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back
        </button>

        <div>
          <h1 className="text-xl font-semibold leading-snug text-gray-900 sm:text-2xl">
            {selectedEvent?.title || 'Select a Signup Event'}
          </h1>
          {selectedEvent && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-4 w-4" aria-hidden />
                {formatWeekdayDateDisplay(selectedEvent.start_date)} – {formatWeekdayDateDisplay(selectedEvent.end_date)}
              </span>
              {lockHours != null && (
                <span className="inline-flex items-center gap-1">
                  <Lock className="h-4 w-4" aria-hidden />
                  Changes close {describeLockWindow(lockHours)} before each meeting
                </span>
              )}
            </div>
          )}
          {description && (
            <div className="mt-3">
              <p className={`whitespace-pre-line text-sm leading-relaxed text-gray-600 sm:text-base ${
                descriptionIsLong && !showFullDescription ? 'line-clamp-4' : ''
              }`}>
                {description}
              </p>
              {descriptionIsLong && (
                <button
                  type="button"
                  onClick={() => setShowFullDescription((v) => !v)}
                  className="mt-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  {showFullDescription ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {notice && (
        <div role="status" className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
          <span>{notice}</span>
        </div>
      )}

      {cancelRegistrationMutation.isError && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {cancelRegistrationMutation.error?.message || 'Could not cancel the booking'}
        </div>
      )}

      {/* This round's bookings */}
      {thisEventRegistrations.length > 0 && (
        <section aria-labelledby="your-bookings" className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
          <h2 id="your-bookings" className="mb-3 text-base font-semibold text-gray-900">Your bookings</h2>
          <ul className="divide-y divide-gray-100">
            {thisEventRegistrations.map((registration) => {
              const slot = registration.signup_slots;
              const name = `${registration.students?.first_name ?? ''} ${registration.students?.last_name ?? ''}`.trim();
              const started = slot ? hasSlotStarted(slot, now) : false;
              const locked = slot ? isSlotLocked(slot, lockHours, now) : false;
              const deadline = slot ? slotLockDeadline(slot, lockHours) : null;
              const isMoving = movingRegistrationId === registration.id;
              const isCancelling = cancelRegistrationMutation.isPending
                && cancelRegistrationMutation.variables?.registrationId === registration.id;
              return (
                <li key={registration.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{name}</p>
                      {slot && (
                        <p className="mt-0.5 text-sm tabular-nums text-gray-700">{formatSlot(slot)}</p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <MeetingModeBadge mode={slot?.meeting_mode} />
                        {started ? (
                          <span className="text-xs text-gray-500">This meeting has started</span>
                        ) : locked ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Lock className="h-3 w-3" aria-hidden />
                            Locked · contact the office to change
                          </span>
                        ) : deadline ? (
                          <span className="text-xs text-gray-500">Can change until {formatLockDeadline(deadline)}</span>
                        ) : null}
                      </div>
                      {registration.notes && (
                        <p className="mt-1 text-sm text-gray-500">Notes: {registration.notes}</p>
                      )}
                    </div>
                    {/* Either parent can change or cancel a booking. It used to be only
                        whoever made it, which left a co-parent looking at a time they
                        could not move and no explanation why. */}
                    {!started && !locked && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => (isMoving ? stopMove() : startMove(registration))}
                          disabled={moveRegistrationMutation.isPending}
                          className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {isMoving ? 'Keep this time' : 'Change time'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelRegistration(registration.id, name, slot ? formatSlot(slot) : 'this time')}
                          disabled={cancelRegistrationMutation.isPending}
                          className="rounded-full px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          {isCancelling ? 'Canceling...' : 'Cancel'}
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {!showForm && (
        <p className="rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-600">
          {everyoneBooked
            ? 'Everyone is booked. To pick a different time, use Change time above.'
            : 'This round isn’t for any of your children.'}
        </p>
      )}

      {/* Booking form — doubles as the "move an existing booking" form. */}
      {showForm && (
        <section id="slot-picker" aria-labelledby="slot-picker-heading" className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
          {movingRegistration ? (
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 id="slot-picker-heading" className="text-base font-semibold text-gray-900">
                  Change {movingRegistration.students?.first_name}&rsquo;s time
                </h2>
                <p className="mt-0.5 text-sm text-gray-600">
                  Currently {movingRegistration.signup_slots && formatSlot(movingRegistration.signup_slots)}.
                  {' '}The current time is kept until the new one is confirmed.
                </p>
              </div>
              <button
                type="button"
                onClick={stopMove}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Keep current time
              </button>
            </div>
          ) : (
            <h2 id="slot-picker-heading" className="mb-4 text-base font-semibold text-gray-900">Book a time</h2>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Student — hidden while moving; the child is already chosen. */}
            {!movingRegistration && (
              <fieldset>
                <legend className="mb-2 text-sm font-medium text-gray-700">Who is this for?</legend>
                <div className="flex flex-wrap gap-2">
                  {availableStudents.map((student) => {
                    const checked = selectedStudentId === student.id;
                    return (
                      <label
                        key={student.id}
                        className={`cursor-pointer rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors focus-within:ring-2 focus-within:ring-blue-400 ${
                          checked
                            ? 'border-blue-500 bg-blue-50 text-blue-800'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="student"
                          value={student.id}
                          checked={checked}
                          onChange={(e) => setSelectedStudentId(e.target.value)}
                          className="sr-only"
                        />
                        {student.first_name} {student.last_name}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            )}

            {/* Day, then time */}
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-gray-700">Pick a time</legend>

              {lockHours != null && (
                <p className="mb-2 text-xs text-gray-500">
                  Times within {describeLockWindow(lockHours)} aren&rsquo;t shown — bookings close {describeLockWindow(lockHours)} before each meeting.
                </p>
              )}

              {slotsLoading ? (
                <p className="py-6 text-center text-sm text-gray-500">Loading available times...</p>
              ) : days.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">
                  No open times left. Please contact the office if you need one.
                </p>
              ) : (
                <>
                  {days.length > 1 && (
                    <div className="-mx-4 mb-3 overflow-x-auto px-4 sm:mx-0 sm:px-0" role="group" aria-label="Day">
                      <div className="flex gap-2">
                        {days.map((day) => {
                          const active = day === selectedDay;
                          const open = (slotsByDate.get(day) ?? []).length;
                          const [weekday, monthDay] = formatWeekdayDateDisplay(day).split(', ');
                          return (
                            <button
                              key={day}
                              type="button"
                              aria-pressed={active}
                              onClick={() => setSelectedDay(day)}
                              className={`flex min-w-[4.5rem] flex-shrink-0 flex-col items-center rounded-lg border px-3 py-2 text-center transition-colors ${
                                active
                                  ? 'border-blue-500 bg-blue-50 text-blue-800'
                                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <span className="text-xs font-medium uppercase tracking-wide">{weekday}</span>
                              <span className="text-sm font-semibold">{monthDay}</span>
                              <span className={`text-[11px] ${active ? 'text-blue-700' : 'text-gray-500'}`}>{open} open</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <p className="mb-2 text-sm font-medium text-gray-800">
                    {formatWeekdayDateDisplay(selectedDay, { weekday: 'long' })}
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {daySlots.map((slot) => {
                      const isCurrent = movingRegistration?.slot_id === slot.id;
                      const checked = selectedSlotId === slot.id;
                      return (
                        <label
                          key={slot.id}
                          className={`flex cursor-pointer flex-col items-start gap-1 rounded-lg border p-3 transition-colors focus-within:ring-2 focus-within:ring-blue-400 ${
                            checked
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="slot"
                            value={slot.id}
                            checked={checked}
                            onChange={(e) => setSelectedSlotId(e.target.value)}
                            className="sr-only"
                          />
                          <span className={`text-sm font-semibold tabular-nums ${checked ? 'text-blue-800' : 'text-gray-900'}`}>
                            {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                          </span>
                          <span className="flex flex-wrap items-center gap-1">
                            <MeetingModeBadge mode={slot.meeting_mode} />
                            {isCurrent && (
                              <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600">
                                Current
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </fieldset>

            {/* Notes — a new booking only; a move keeps the booking's notes. */}
            {!movingRegistration && (
              <div>
                <label htmlFor="notes" className="mb-1.5 block text-sm font-medium text-gray-700">
                  Notes for the teacher <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Anything you'd like to discuss"
                />
              </div>
            )}

            {submitError && (
              <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={
                !selectedEventId || !selectedSlotId || slotsLoading
                || (!movingRegistration && !selectedStudentId)
                || registerMutation.isPending || moveRegistrationMutation.isPending
              }
              className="w-full rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:ml-auto sm:block sm:w-auto sm:py-2.5"
            >
              {moveRegistrationMutation.isPending ? 'Moving...' :
               registerMutation.isPending ? 'Registering...' :
               slotsLoading ? 'Loading slots...' :
               !selectedSlot ? (movingRegistration ? 'Pick a new time' : 'Pick a time') :
               movingRegistration ? `Move to ${formatWeekdayDateDisplay(selectedSlot.slot_date)}, ${formatTime(selectedSlot.start_time)}` :
               `Book ${formatWeekdayDateDisplay(selectedSlot.slot_date)}, ${formatTime(selectedSlot.start_time)}`}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
